/**
 * BBW-198: hide the add-to-cart button on client-rendered search results
 * for low-stock products.
 *
 * Two search UIs render add-to-cart buttons outside the theme's Liquid:
 *  - Searchanise ("snize"), injected by the app at runtime — the live
 *    autocomplete + search results. Its API knows nothing about metafields,
 *    but each result links to the product.
 *  - the custom Rebuy Smart Search bar (assets/search-bar.js), same problem.
 *
 * The theme's metafield gates (custom.alacsony_keszletu_termek) never run for
 * these cards. This module watches for result cards, asks the theme for
 * each product's flag via the Section Rendering API (sections/low-stock-flag.liquid,
 * rendered in product context), and reveals the button only for purchasable
 * products. Buttons are hidden by default via CSS (see custom.css) so a blocked
 * product's button is never clickable, not even briefly.
 *
 * Fail-open on errors: if the flag cannot be fetched, the button is revealed
 * (pre-fix behaviour) rather than breaking purchases for everyone.
 */

const BUTTON_SELECTOR = '.snize-add-to-cart-button, .search-results__product-card__add-to-cart';
const REVEAL_CLASS = 'low-stock-checked';
const BLOCKED_CLASS = 'low-stock-blocked';

const root = window.Shopify?.routes?.root ?? '/';
const pending = new Map(); // handle -> Promise<boolean>

const readSession = (key) => {
	try {
		return sessionStorage.getItem(key);
	} catch {
		return null; // private mode / storage disabled
	}
};

const writeSession = (key, value) => {
	try {
		sessionStorage.setItem(key, value);
	} catch {
		/* ignore */
	}
};

const handleFromCard = (button) => {
	const link = button.closest('a[href*="/products/"]')
		?? button.closest('.snize-product, [class*="search-results__product-card"], li, article')?.querySelector('a[href*="/products/"]');
	return link?.getAttribute('href')?.match(/\/products\/([a-z0-9\-_.]+)/i)?.[1] ?? null;
};

const isLowStock = (handle) => {
	if (pending.has(handle)) return pending.get(handle);

	const cached = readSession(`lowstock:${handle}`);
	if (cached !== null) {
		const promise = Promise.resolve(cached === 'true');
		pending.set(handle, promise);
		return promise;
	}

	const promise = (async () => {
		try {
			const res = await fetch(`${root}products/${handle}?section_id=low-stock-flag`);
			if (!res.ok) return false;
			const html = await res.text();
			const blocked = html.includes('data-low-stock="true"');
			writeSession(`lowstock:${handle}`, String(blocked));
			return blocked;
		} catch {
			return false; // fail-open
		}
	})();

	pending.set(handle, promise);
	return promise;
};

const processButton = async (button) => {
	if (button.classList.contains(REVEAL_CLASS) || button.classList.contains(BLOCKED_CLASS)) return;

	const handle = handleFromCard(button);
	if (!handle) {
		button.classList.add(REVEAL_CLASS); // can't identify the product — fail-open
		return;
	}

	const blocked = await isLowStock(handle);
	button.classList.add(blocked ? BLOCKED_CLASS : REVEAL_CLASS);
};

const scan = (node) => {
	if (!(node instanceof Element)) return;
	if (node.matches(BUTTON_SELECTOR)) processButton(node);
	node.querySelectorAll(BUTTON_SELECTOR).forEach(processButton);
};

new MutationObserver((mutations) => {
	for (const { addedNodes } of mutations) {
		addedNodes.forEach(scan);
	}
}).observe(document.body, { childList: true, subtree: true });

scan(document.body);
